<!-- Send Email to an attendee (fullstack claude/ATTENDEES-SPEC.md § 5), from the year's People page
     (People/Attendees.vue), laid out as Apple Mail's compose window (Seán, 2026-11-03): inline
     labels with colons, each value straight after its label (not in a column), the addresses
     as grey, disabled-looking chips, borderless fields, a hairline between rows; To and Reply-To read-only,
     Subject and the message editable; what Send does in the footer's note, above the buttons. The
     dialog's standard width. Opened by #contact-attendee&<kind>&<id>&from=<the page's filter>;
     closing returns to that filter (closeHash), and Back closes it as well (the hash was pushed).
     The server sends it, with the addresses it reads from Clerk: Reply-To the sender (the address
     shown here), a copy to the sender, the recipient's address never shown. Mounted once, with
     the other dialogs (Dialogs.vue): one inside the page would be kept alive with it, detached,
     and fail to open. -->

<template>
  <AppModalDialog :hash="HASH" :close-hash="closeHash" :is-held-open="isSending" @closed="reset">
    <AppFormWrapper>
      <h2 class="title">{{ isSent ? "Message Sent" : "Send Email" }}</h2>
      <p v-if="!recipient" class="text">Loading…</p>
      <p v-else-if="isSent" class="text">Sent to {{ recipientName }}. A copy is in your inbox.</p>
      <div v-else class="compose">
        <div class="row">
          <span class="label">To:</span>
          <span class="value token">{{ recipientName }}</span>
        </div>
        <div class="row">
          <span class="label">Reply-To:</span>
          <span class="value token">{{ senderEmail }}</span>
        </div>
        <div class="row">
          <label class="label" :for="`${fieldId}-subject`">Subject:</label>
          <input
            :id="`${fieldId}-subject`"
            v-model="subject"
            class="value input"
            :maxlength="MAX_SUBJECT_LENGTH"
            required
            :disabled="isSending" />
        </div>
        <label class="visuallyHidden" :for="`${fieldId}-message`">Message</label>
        <textarea
          :id="`${fieldId}-message`"
          v-model="message"
          class="input message"
          rows="6"
          placeholder="Your message"
          :maxlength="MAX_LENGTH"
          :disabled="isSending"
          :aria-describedby="`${fieldId}-count ${fieldId}-note`" />
        <p :id="`${fieldId}-count`" class="count">{{ message.length }} / {{ MAX_LENGTH }}</p>
        <p v-if="error" class="error" role="alert">{{ error }}</p>
      </div>
    </AppFormWrapper>
    <template #footer>
      <!-- what Send does, just above it: the footer's note, as #sign-in's and the email
           change's (Seán, 2026-11-03: about the button, not the fields) -->
      <div v-if="recipient && !isSent" :id="`${fieldId}-note`" class="note">
        <p>VIZBI will forward your message to {{ recipientName }}.</p>
        <p>They will be able to reply directly to your address.</p>
      </div>
      <div class="footer-buttons">
        <ButtonBespoke v-if="isSent" outstanding @click="close">Close</ButtonBespoke>
        <template v-else>
          <ButtonBespoke :disabled="isSending" @click="close">Cancel</ButtonBespoke>
          <ButtonBespoke outstanding :disabled="!canSend" @click="send">
            {{ isSending ? "Sending…" : "Send" }}
          </ButtonBespoke>
        </template>
      </div>
    </template>
  </AppModalDialog>
</template>

<script setup>
import {computed, ref, useId} from "vue";
import {useRoute, useRouter} from "vue-router";
import AppModalDialog from "@/components/App/Modal/Dialog/index";
import AppFormWrapper from "@/components/App/Form/Wrapper";
import ButtonBespoke from "@/components/ButtonBespoke";
import {authFetch, readJson} from "@/composables/useAuthFetch";
import {getAttendee} from "@/composables/useAttendees";
import useUserSafe from "@/composables/useUserSafe";

const HASH = /^contact-attendee&/;
const MAX_LENGTH = 2000;
const MAX_SUBJECT_LENGTH = 150;
// the subject it opens with, editable; a subject is required (Seán, 2026-11-03: as a LinkedIn
// connection request, a recognisable ask)
const DEFAULT_SUBJECT = "Connecting via VIZBI Online";
const FAILED = "The message couldn't be sent. Please try again in a moment.";

// "#contact-attendee&masterclass&user_123&from=Online-Masterclass-Attendees" → its parts
function getHashParts(hash) {
  const [, kind = "", id = "", ...rest] = decodeURIComponent(hash || "").split("&");
  const from = rest.find((part) => part.startsWith("from="))?.slice(5) || "";
  return {kind, id, from};
}

const [route, router] = [useRoute(), useRouter()];
const {user} = useUserSafe();
const fieldId = useId();
const [subject, message, error] = [ref(DEFAULT_SUBJECT), ref(""), ref("")];
const [isSending, isSent] = [ref(false), ref(false)];

const parts = computed(() => getHashParts(route.hash));
const recipient = computed(() => getAttendee(parts.value.kind, parts.value.id));
const recipientName = computed(() => {
  return [recipient.value?.firstName, recipient.value?.lastName].filter(Boolean).join(" ");
});
const senderEmail = computed(() => user.value?.primaryEmailAddress?.emailAddress || "");
const closeHash = computed(() => parts.value.from);
const canSend = computed(() => {
  const isFilled = Boolean(subject.value.trim() && message.value.trim());
  return Boolean(recipient.value?.isContactable) && isFilled && !isSending.value;
});

function reset() {
  [subject.value, message.value] = [DEFAULT_SUBJECT, ""];
  [error.value, isSent.value] = ["", false];
}

function close() {
  const hash = closeHash.value ? `#${closeHash.value}` : "";
  router.replace({path: route.path, hash});
}

async function postMessage() {
  const body = {kind: parts.value.kind, year: Number(route.params.year), subject: subject.value,
    message: message.value, recipientId: parts.value.id};
  const headers = {"Content-Type": "application/json"};
  return await authFetch("/api/v1/user/attendees/contact", {method: "POST", headers,
    body: JSON.stringify(body)});
}

async function send() {
  if (!canSend.value) return;
  [isSending.value, error.value] = [true, ""];
  const response = await postMessage();
  isSending.value = false;
  if (response.ok) return (isSent.value = true);
  const {message: reason} = await readJson(response);
  error.value = response.status >= 500 || !reason ? FAILED : `${reason}.`;
}
</script>

<style scoped>
/* Apple Mail's compose rows: label, then its value right after it, a hairline below; no boxes */
.compose {
  --rule-color: #a8a8a8; /* ▶ EDIT HERE: the lines between rows (a step darker: Seán) */
  --rule: 1px solid var(--rule-color);
  text-align: left;
}
.title,
.text,
.count,
.error,
.note {
  text-align: left;
}
.row {
  display: flex;
  align-items: center;
  gap: 6px; /* ▶ EDIT HERE: from a label to its value */
  min-height: 44px; /* ▶ EDIT HERE: each row's height (a comfortable touch target) */
  border-bottom: var(--rule);
}
.label {
  flex: none;
  color: #787878;
}
.value {
  min-width: 0;
  color: #111;
  overflow-wrap: anywhere;
}
/* a name or address, as Mail's chip, but disabled-looking: fixed, nothing to click (Seán,
   2026-11-03: a tint of the brand colour read as "click here"); a neutral grey, the text faded,
   the not-allowed cursor (DESIGN.md § 15) */
.token {
  padding: 1px 8px;
  border-radius: 5px;
  background: #d4d4d4;
  color: #6a6a6a;
  cursor: not-allowed;
  user-select: text;
}
/* the form wrapper's input look (a box, a margin) undone: a borderless field, as Mail's */
.input {
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 10px 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
  font: inherit;
  font-size: 1rem;
  line-height: 1.4;
  color: #111;
}
.input:focus {
  outline: none;
  border: none;
  box-shadow: none; /* not the form wrapper's green box: the row's line shows the focus */
}
.row:focus-within {
  border-bottom-color: var(--brand-dark-color);
}
.message {
  display: block;
  padding: 9px 0 4px; /* ▶ EDIT HERE: above the message's first line */
  /* no sides, no corners, as Mail's: the focus colours the lines above and below it only; the
     top line is its own, laid over the Subject row's */
  margin-top: -1px;
  border-top: 1px solid transparent;
  resize: none; /* the grip only once it's in use */
}
.message:focus {
  border-top: 1px solid var(--brand-dark-color); /* whole: .input:focus took the border off */
  resize: vertical;
}
.message:focus + .count {
  border-top-color: var(--brand-dark-color);
}
.message::placeholder {
  color: #8a8a8a;
}
.count {
  margin: 0;
  padding-top: 4px;
  border-top: var(--rule);
  font-size: 0.8rem;
  color: #787878;
  text-align: right;
}
.error {
  margin: 4px 0 0;
  color: #c62828;
}
.note {
  width: 100%;
  margin: 0 0 20px; /* ▶ EDIT HERE: from the note to the buttons */
}
/* left-aligned, across the buttons' width; the colour, every footer note's (Dialog/index.vue) */
.note p {
  margin: 0;
  text-align: left;
  font-size: 14px;
  line-height: 1.4;
}
.note p + p {
  margin-top: 4px; /* ▶ EDIT HERE: between the note's two paragraphs */
}
.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
